"use client";
import { Switch as Toggle } from "@headlessui/react";
import clsx from "clsx";
import { forwardRef } from "react";

export type SwitchSize = "sm" | "md";

export type SwitchLabelPosition = "start" | "end";

export type SwitchProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  name?: string | undefined;
  value?: string | undefined;
  labelPosition?: SwitchLabelPosition;
  size?: SwitchSize;
};

const textSizes: Record<SwitchSize, string> = {
  sm: "text-app-body-2",
  md: "text-body-2",
};

const switchSizes: Record<SwitchSize, string> = {
  sm: "h-3s w-2m",
  md: "h-m w-l",
};

const dotSizes: Record<SwitchSize, string> = {
  sm: "h-2s w-2s",
  md: "h-[28px] w-[28px]",
};

const dotCheckedSizes: Record<SwitchSize, string> = {
  sm: "translate-x-s",
  md: "translate-x-3s",
};

const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { size = "sm", label, labelPosition = "start", ...props },
  ref,
) {
  const labelEl = label && <span className={textSizes[size]}>{label}</span>;

  return (
    <div className="flex items-center gap-xs">
      <Toggle
        {...props}
        ref={ref}
        data-testid={props.name}
        className="flex items-center gap-2xs text-left"
      >
        {labelPosition === "start" && labelEl}
        <div
          className={clsx(
            "relative flex items-center rounded-full p-[2px] transition",
            switchSizes[size],
            {
              "bg-primary-800": props.checked,
              "bg-neutral-150": !props.checked,
            },
          )}
        >
          <span
            className={clsx(
              "inline-block transform rounded-full bg-neutral-0 transition",
              dotSizes[size],
              {
                [dotCheckedSizes[size]]: props.checked,
              },
            )}
          />
        </div>
        {label && labelPosition === "end" && labelEl}
      </Toggle>
    </div>
  );
});

export default Switch;
